import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { createSalesCode, getAllSalesCodes } from '../api/salesCode.api.js';
import { Handshake } from 'lucide-react';

interface SalesCode {
  id: string;
  nombre: string;
  apellido: string;
  codigo: string;
}

const SalesCodes = () => {
  const [salesCodes, setSalesCodes] = useState<SalesCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [codigo, setCodigo] = useState('');

  const fetchSalesCodes = async () => {
    try {
      const data = await getAllSalesCodes();
      setSalesCodes(data);
    } catch (error) {
      console.error('Error fetching sales codes:', error);
      toast.error('Error al obtener los códigos de vendedores.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesCodes();
  }, []);

  const resetForm = () => {
    setNombre('');
    setApellido('');
    setCodigo('');
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (codigo.trim().length !== 3) {
      toast.error('El código debe tener exactamente 3 caracteres.');
      return;
    }

    setSaving(true);
    try {
      await createSalesCode({ nombre, apellido, codigo });
      toast.success('Código de vendedor creado exitosamente.');
      resetForm();
      fetchSalesCodes();
    } catch (error: any) {
      if (error?.response?.status === 409) {
        toast.error('El código ya existe.');
      } else {
        toast.error('Error al crear el código de vendedor.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Códigos de Vendedores</h1>
      </div>

      <div className="bg-white shadow-md rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Handshake className="w-5 h-5" />
          Nuevo código de vendedor
        </h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
          <div>
            <label htmlFor="nombre" className="block text-gray-700 text-sm font-medium mb-1">Nombre</label>
            <input
              type="text"
              id="nombre"
              name="nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Nombre"
              required
            />
          </div>

          <div>
            <label htmlFor="apellido" className="block text-gray-700 text-sm font-medium mb-1">Apellido</label>
            <input
              type="text"
              id="apellido"
              name="apellido"
              value={apellido}
              onChange={(e) => setApellido(e.target.value)}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none"
              placeholder="Apellido"
              required
            />
          </div>

          <div>
            <label htmlFor="codigo" className="block text-gray-700 text-sm font-medium mb-1">Código (3 caracteres)</label>
            <input
              type="text"
              id="codigo"
              name="codigo"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.toUpperCase())}
              className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500 outline-none uppercase"
              placeholder="ABC"
              maxLength={3}
              required
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="bg-green-500 text-white px-6 py-2 min-h-[44px] lg:min-h-0 rounded-md shadow-md hover:bg-green-600 focus:outline-none disabled:bg-green-300"
          >
            {saving ? 'Creando...' : 'Crear código'}
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      ) : salesCodes.length === 0 ? (
        <div className="text-center py-10 text-gray-500">
          No hay códigos de vendedores disponibles.
        </div>
      ) : (
        <div className="overflow-x-auto bg-white shadow-md rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 text-sm">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Apellido</th>
                <th className="px-4 py-2 text-left font-medium text-gray-500 uppercase tracking-wider">Código</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 text-sm">
              {salesCodes.map((salesCode) => (
                <tr key={salesCode.id}>
                  <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{salesCode.nombre}</td>
                  <td className="px-4 py-2 text-gray-700 whitespace-nowrap">{salesCode.apellido}</td>
                  <td className="px-4 py-2 text-gray-700 whitespace-nowrap font-mono">{salesCode.codigo}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default SalesCodes;
